// TODO: resolve explicit any types once main components get updated to .tsx
import React from 'react';
import { View } from 'react-native';
import i18n from 'i18n-js';
import moment from 'moment';

import { formatTimeEvent, convertTimeLetter, JOB_STATUSES_TEXT, JOB_STATUSES, localTime } from '@helpers';
import { DATA_GRID_IMAGE_COLUMN_WIDTH, getJobOrOrderStatusConfig, TEST_IDS, TILE_CONFIG } from '@v2/pages/common/constants/base.constants';
import { DOCUMENT_ENTITY_TYPE, IMAGE_HANDLING_CONTEXT, KEYS_TAGS } from '@components/imageHandling/common';
import { LevelSubScreen } from '@v2/pages/common/enums';
import Image from '@v2/context/Image/context';
import StatusBadge from '@components/common/StatusBadge';

import type { ScaleFunction } from '@components/operatorPanel/interfaces';
import type { ColorFunction } from '@styles/colors/types';
import type { OperationImageDataParams, DataGridColumnsParams } from '@components/search/operations/types';

export const getScheduleAdherenceInfo = ({
  amountOfDays,
  amountOfMinutes,
  isNotStarted,
}: {
  amountOfDays: number;
  amountOfMinutes?: number | null;
  isNotStarted: boolean;
}) => {
  const abs = Math.abs(amountOfDays);
  let showBehindError = false;
  let badgeIcon: string | null = null;

  const offsetText = (() => {
    if (amountOfDays === 0 && amountOfMinutes != null) {
      if (amountOfMinutes > 0) {
        showBehindError = true;
        badgeIcon = 'hoursLate';
        const timeText = formatTimeEvent(convertTimeLetter(amountOfMinutes * 60), false).trim();
        return i18n.t('{{time}} Behind', { time: timeText });
      }
      badgeIcon = 'inTime';
      return i18n.t('On schedule');
    }

    if (isNotStarted) {
      if (amountOfDays == 0) return i18n.t('Start Today');
      if (amountOfDays < 0) return i18n.t('{{amount}} Days to Start', { amount: abs });
      showBehindError = true;
      return i18n.t('{{amount}} Days Behind', { amount: abs });
    } else {
      if (amountOfDays == 0) return i18n.t('On Time');
      if (amountOfDays < 0) return i18n.t('{{amount}} Days Ahead', { amount: abs });
      showBehindError = true;
      return i18n.t('{{amount}} Days Behind', { amount: abs });
    }
  })();

  const badgeText = badgeIcon ? null : isNotStarted && amountOfDays === 0 ? 'S' : String(abs);
  const badgeLabelText = badgeIcon ? offsetText : amountOfDays === 0 ? offsetText : offsetText.replace(String(abs), '').trim();

  return { abs, offsetText, badgeText, badgeIcon, badgeLabelText, showBehindError };
};

export const getPreferredMachine = (machines: any[]) => {
  if (!machines || machines.length === 0) return null;

  const priority: Record<string, number> = {
    ProducedOn: 1,
    Preferred: 2,
    Alternative1: 3,
    Alternative2: 4,
  };

  return machines.slice().sort((a: any, b: any) => (priority[a.linkType] || 99) - (priority[b.linkType] || 99))[0];
};

export const getStatusConfig = (status: string, C: ColorFunction, scale: ScaleFunction) => {
  const jobStatusConfig = {
    running: {
      condition: status === JOB_STATUSES.RUNNING,
      state: {
        styles: { backgroundColor: C('ProdJobBatch/Light/Running'), color: C('Label/Light/AlwaysLight Primary') },
        text: JOB_STATUSES_TEXT[JOB_STATUSES.RUNNING],
        icon: 'runningMan',
        width: 9,
        height: 10.5,
        transform: 'translate(0, -0.5)',
      },
    },
    paused: {
      condition: status === JOB_STATUSES.PAUSED,
      state: {
        styles: { backgroundColor: C('ProdJobBatch/Light/Paused'), color: C('Label/Light/AlwaysLight Primary') },
        text: JOB_STATUSES_TEXT[JOB_STATUSES.PAUSED],
        icon: 'pause',
        width: 6,
        height: 12,
        transform: 'translate(0, -0.3)',
      },
    },
    finished: {
      condition: status === JOB_STATUSES.FINISHED,
      state: {
        styles: { backgroundColor: C('ProdJobBatch/Light/Finished'), color: C('Label/Light/AlwaysLight Primary') },
        text: JOB_STATUSES_TEXT[JOB_STATUSES.FINISHED],
        icon: 'finishFlag',
        width: 9.5,
        height: 10,
        transform: 'translate(0, -0.5)',
      },
    },
    skipped: {
      condition: status === JOB_STATUSES.SKIPPED,
      state: {
        styles: { backgroundColor: C('Status/Light/Not Productive'), color: C('Label/Light/AlwaysLight Primary') },
        text: JOB_STATUSES_TEXT[JOB_STATUSES.SKIPPED],
        icon: 'skipped',
        width: 12,
        height: 7,
      },
    },
    notStarted: {
      condition: status === JOB_STATUSES.NOT_STARTED,
      state: {
        styles: {
          backgroundColor: 'transparent',
          color: C('Status/Light/Not Productive'),
          borderColor: C('Status/Light/Not Productive'),
          borderWidth: scale(1),
          borderStyle: 'dashed',
          textContainerStyles: { transform: [{ translateY: scale(-1) }] },
        },
        text: JOB_STATUSES_TEXT[JOB_STATUSES.NOT_STARTED],
        icon: 'dotsClock',
        width: 15,
        height: 19,
      },
    },
    start: {
      condition: status === JOB_STATUSES.START,
      state: {
        styles: { backgroundColor: C('Status/Light/Active'), color: C('Label/Light/AlwaysLight Primary') },
        text: JOB_STATUSES_TEXT[JOB_STATUSES.START],
        icon: 'play',
        width: 7,
        height: 9,
      },
    },
  };

  return jobStatusConfig;
};

export const getOperationImageData = ({ item, imgSize, onPress, contextId, borderRadius }: OperationImageDataParams) => {
  const imageData = {
    image: {
      documentId: item.imageDocumentId,
      imageHash: item.imageHash,
      imageData: {
        isCropped: item.imageData?.isCropped,
        extensionDerivative: item.imageData?.extensionDerivative,
        pageNumber: item.imageData?.pageNumber,
        rotationAngle: item.imageData?.rotationAngle || 0,
        rotationAngleCrop: item.imageData?.rotationAngle || 0,
      },
      width: imgSize.width,
      height: imgSize.height,
      contextId,
      contextCode: IMAGE_HANDLING_CONTEXT[DOCUMENT_ENTITY_TYPE.ProductionOrder],
      ...(borderRadius !== undefined && { borderRadius }),
    },
    onPress,
  };

  return imageData;
};

export const buildAttachPayload = (item: { productionOperationId: number; productionOperationNumber: string }, attachImageId: number) => ({
  documentId: attachImageId,
  documentLinks: [
    {
      scopeId: KEYS_TAGS.Operation,
      scopeReferenceId: item.productionOperationId,
      scopeReferenceName: item.productionOperationNumber,
      isFavorite: false,
      isActivated: true,
      isFavoriteChanged: false,
    },
  ],
  documentTags: [],
});

export const getOperationImageSize = (isLineMode: boolean, itemSize: number, scale: ScaleFunction) => {
  if (isLineMode) {
    return { width: scale(TILE_CONFIG.LINE_MODE_WIDTH), height: scale(TILE_CONFIG.LINE_MODE_HEIGHT) };
  }
  const prop = itemSize / TILE_CONFIG.BASE_WIDTH;
  const newHeight = TILE_CONFIG.BASE_HEIGHT * prop;
  return { width: itemSize, height: newHeight };
};

export const getOperationItemStyle = (isEmpty: boolean, isSmallMode: boolean, index: number, itemSize: number | undefined, scale: ScaleFunction) => {
  const isWeb = require('@helpers').isWeb;
  const style: Record<string, any> = {
    opacity: isEmpty ? 0 : 1,
    marginBottom: !isWeb && index === 0 ? 0 : scale(isSmallMode ? TILE_CONFIG.MARGIN_BOTTOM_SMALL : TILE_CONFIG.MARGIN_BOTTOM_NORMAL),
  };
  if (itemSize) {
    style.width = itemSize;
  }
  return style;
};

export const getProgressBarWidths = (totalTimeActualSeconds: number, totalTimeScheduledSeconds: number, baseLineWidth: number) => {
  const blockDuration = Math.max(totalTimeActualSeconds, totalTimeScheduledSeconds);
  const blackBlock = Math.min(totalTimeActualSeconds, totalTimeScheduledSeconds);
  const redBlock = Math.max(totalTimeActualSeconds - totalTimeScheduledSeconds, 0);
  const progressWidth = blockDuration > 0 ? (blackBlock / blockDuration) * baseLineWidth : 0;
  const redProgressWidth = blockDuration > 0 ? (redBlock / blockDuration) * baseLineWidth : 0;
  return { blockDuration, blackBlock, redBlock, progressWidth, redProgressWidth };
};

export const getScheduleAdherenceDate = (item: {
  scheduleExceedsOn?: string | null;
  status: string;
  scheduledStartDate?: string | null;
  scheduledEndDate?: string | null;
}) => {
  return (
    item.scheduleExceedsOn ?? (item.status === JOB_STATUSES.NOT_STARTED ? item.scheduledStartDate : item.scheduledEndDate) ?? item.scheduledStartDate
  );
};

// Resolves the reference date, diffs it against the factory-time "now", and maps the result to the adherence descriptor in one call.
export const getScheduleAdherence = (item: Parameters<typeof getScheduleAdherenceDate>[0], nowRef?: string | Date | number) => {
  const now = localTime(nowRef);
  const scheduleAdherenceDate = getScheduleAdherenceDate(item);
  const amountOfDays = now.diff(scheduleAdherenceDate, 'day');
  const amountOfMinutes = now.diff(scheduleAdherenceDate, 'minute');
  return getScheduleAdherenceInfo({ amountOfDays, amountOfMinutes, isNotStarted: item.status === JOB_STATUSES.NOT_STARTED });
};

export const getActionButtonConfig = (isRunning: boolean, isNotStarted: boolean) => {
  const icon = isRunning ? 'pauseOP' : 'startOP';
  const label = isRunning ? 'Pause' : isNotStarted ? 'Start' : 'Resume';
  const bg = isRunning ? 'Label/Light/Primary' : 'Status/Light/Active';
  const hoverColor = isRunning ? 'ProdJobBatch/Light/Paused' : 'Status/Light/Active';
  return { icon, label, bg, hoverColor };
};

export const getDateDisplay = (item: {
  status: string;
  actualStartDate?: string | null;
  expectedStartOn?: string | null;
  scheduledStartDate?: string | null;
}) => {
  const isRunning = item.status === 'Running';
  const label = isRunning ? 'Actual Start Date' : item.status === JOB_STATUSES.PAUSED ? 'Expected Resume Date' : 'Expected Start Date';
  const value = isRunning ? item.actualStartDate : item.expectedStartOn || item.scheduledStartDate;
  return { label, value };
};

export const deduplicateByProperty = <T extends Record<string, any>>(primaryArray: T[], secondaryArray: T[], propertyName: string): T[] => {
  const seen = new Set();
  const result: T[] = [];

  primaryArray.forEach((item) => {
    const propertyValue = item[propertyName];
    if (!seen.has(propertyValue)) {
      seen.add(propertyValue);
      result.push(item);
    }
  });

  secondaryArray.forEach((item) => {
    const propertyValue = item[propertyName];
    if (!seen.has(propertyValue)) {
      seen.add(propertyValue);
      result.push(item);
    }
  });

  return result;
};

export const getDataGridColumns = ({ scale, font, C, open, attachMode }: DataGridColumnsParams) => [
  {
    field: 'Image',
    headerName: '',
    align: 'center',
    width: scale(DATA_GRID_IMAGE_COLUMN_WIDTH),
    sortable: false,
    filterable: false,
    renderCell: ({ row }: { row: any }) => {
      const imgSize = { width: scale(50), height: scale(30) };
      const item = row;
      const imageData = getOperationImageData({
        item,
        imgSize,
        onPress: () => open(item, LevelSubScreen.DOCUMENTS),
        contextId: item.ncFileId,
      });

      return (
        <View
          testID={TEST_IDS.searchScreen.operations.dataGridImage}
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}
        >
          <Image {...imageData} attachMode={attachMode} />
        </View>
      );
    },
  },
  { field: 'productionOrderNumber', headerName: i18n.t('Production Order'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'operationNumber', headerName: i18n.t('Operation'), align: 'left', width: scale(175), sortable: true, filterable: true },
  { field: 'operationName', headerName: i18n.t('Operation Name'), align: 'left', width: scale(225), sortable: true, filterable: true },

  {
    field: 'machines',
    headerName: i18n.t('Machine'),
    align: 'left',
    width: scale(150),
    sortable: false,
    filterable: false,
    valueFormatter: (value: any) => (value ? getPreferredMachine(value)?.machineName : ''),
  },
  {
    field: 'status',
    headerName: i18n.t('Status'),
    type: 'singleSelect',
    valueOptions: Object.values(getJobOrOrderStatusConfig(JOB_STATUSES.RUNNING, C)).map(({ state }) => ({
      value: state.text,
      label: i18n.t(state.text || ''),
    })),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
    renderCell: ({ row: item }: { row: any }) => {
      const jobStatusConfig = getJobOrOrderStatusConfig(item.status, C);

      return (
        <View style={{ flexDirection: 'row', justifyContent: 'flex-start', alignItems: 'center', height: '100%', width: '100%' }}>
          <StatusBadge C={C} config={jobStatusConfig} scale={scale} font={font} />
        </View>
      );
    },
  },
  {
    field: 'scheduledStartDate',
    headerName: i18n.t('Scheduled Start Date'),
    align: 'left',
    width: scale(150),
    type: 'date',
    sortable: true,
    filterable: true,
    valueGetter: (value: any) => (value ? new Date(value) : value),
    valueFormatter: (value: any) => (value ? moment(value).format('DD/MM/YYYY') : ''),
  },
  {
    field: 'scheduledEndDate',
    headerName: i18n.t('Scheduled End Date'),
    align: 'left',
    width: scale(150),
    type: 'date',
    sortable: true,
    filterable: true,
    valueGetter: (value: any) => (value ? new Date(value) : value),
    valueFormatter: (value: any) => (value ? moment(value).format('DD/MM/YYYY') : ''),
  },
  {
    field: 'actualStartDate',
    headerName: i18n.t('Actual Start Date'),
    align: 'left',
    width: scale(150),
    type: 'date',
    sortable: true,
    filterable: true,
    valueGetter: (value: any) => (value ? new Date(value) : value),
    valueFormatter: (value: any) => (value ? moment(value).format('DD/MM/YYYY') : ''),
  },
  {
    field: 'actualEndDate',
    headerName: i18n.t('Actual End Date'),
    align: 'left',
    width: scale(150),
    type: 'date',
    sortable: true,
    filterable: true,
    valueGetter: (value: any) => (value ? new Date(value) : value),
    valueFormatter: (value: any) => (value ? moment(value).format('DD/MM/YYYY') : ''),
  },

  {
    field: 'quantityProduced',
    headerName: i18n.t('Quantity Ready'),
    align: 'left',
    width: scale(200),
    sortable: true,
    filterable: true,
  },
  {
    field: 'quantityTarget',
    headerName: i18n.t('Quantity Demanded'),
    align: 'left',
    width: scale(200),
    sortable: true,
    filterable: true,
  },
  {
    field: 'totalTimeScheduledSeconds',
    headerName: i18n.t('Time Scheduled Total'),
    align: 'left',
    width: scale(200),
    sortable: true,
    filterable: true,
    valueFormatter: (value: any) => (value ? formatTimeEvent(convertTimeLetter(value), false) : ''),
  },
  {
    field: 'totalTimeActualSeconds',
    headerName: i18n.t('Time Elapsed'),
    align: 'left',
    width: scale(200),
    sortable: true,
    filterable: true,
    valueFormatter: (value: any) => (value ? formatTimeEvent(convertTimeLetter(value), false) : ''),
  },
  { field: 'partDescription', headerName: i18n.t('Part Description'), align: 'left', width: scale(150), sortable: true, filterable: false },
  { field: 'partNumber', headerName: i18n.t('Part Name'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'partDrawingNumber', headerName: i18n.t('Drawing Number'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'partDrawingPosition', headerName: i18n.t('Drawing Position'), align: 'left', width: scale(150), sortable: true, filterable: true },
  { field: 'partDrawingRevision', headerName: i18n.t('Drawing Revision'), align: 'left', width: scale(150), sortable: true, filterable: true },
  {
    field: 'linkedNcFiles',
    headerName: i18n.t('Linked NC Files Quantity'),
    align: 'left',
    width: scale(150),
    sortable: false,
    filterable: false,
    valueGetter: (value: any) => (value ? value.length : null),
  },
  {
    field: 'setupTimeSeconds',
    headerName: i18n.t('Time Scheduled Setup'),
    align: 'left',
    width: scale(150),
    sortable: true,
    filterable: true,
    valueFormatter: (value: any) => (value ? formatTimeEvent(convertTimeLetter(value), false) : ''),
  },
  {
    field: 'modifiedOn',
    headerName: i18n.t('Date Modified'),
    align: 'left',
    width: scale(150),
    type: 'date',
    sortable: true,
    filterable: true,
    valueGetter: (value: any) => (value ? new Date(value) : value),
    valueFormatter: (value: any) => (value ? moment(value).format('DD/MM/YYYY') : ''),
  },
  { field: 'modifiedBy', headerName: i18n.t('Last Modified By'), align: 'left', width: scale(175), sortable: true, filterable: true },
  {
    field: 'createdOn',
    headerName: i18n.t('Date Created'),
    align: 'left',
    width: scale(150),
    type: 'date',
    sortable: true,
    filterable: true,
    valueGetter: (value: any) => (value ? new Date(value) : value),
    valueFormatter: (value: any) => (value ? moment(value).format('DD/MM/YYYY') : ''),
  },
  { field: 'createdBy', headerName: i18n.t('Created By'), align: 'left', width: scale(175), sortable: true, filterable: true },
];
